import React from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Modal, InputField, Alert } from '../../../commons';
import { DAV_APIS } from '../../../../adapters';
import { AgentProps } from '../../../../constants';

interface AgentEditModalProps {
  open: boolean;
  onClose: () => void;
  agent: AgentProps;
  onSuccess: () => void;
  mode: 'view' | 'edit';
}

const AgentEditModal: React.FC<AgentEditModalProps> = ({ open, onClose, agent, onSuccess, mode }) => {
  const [isUpdatingStatus, setIsUpdatingStatus] = React.useState(false);
  const isView = mode === 'view';

  const initialValues = {
    username: agent.username || '',
    email: agent.email || '',
    phoneNumber: agent.phoneNumber || '',
    companyName: agent.companyName || '',
    companyWebsite: agent.companyWebsite || '',
    country: agent.country || '',
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string().required('Username is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    phoneNumber: Yup.string().required('Phone number is required'),
    companyName: Yup.string().required('Company name is required'),
    country: Yup.string().required('Country is required'),
  });

  const handleSubmit = async (values: typeof initialValues, { setSubmitting }: any) => {
    try {
      const res = await DAV_APIS.agents.updateAgent(agent.id, values);
      if (res.status === 200) {
        Alert({ message: 'Agent updated successfully', variant: 'success' });
        onSuccess();
        onClose();
      }
    } catch (error) {
      Alert({ message: 'Failed to update agent', variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async () => {
    setIsUpdatingStatus(true);
    try {
      const res = await DAV_APIS.agents.activateDeactivateAgent(
        agent.id,
        agent.isActive ? 'deactivate' : 'activate',
      );
      if (res.status === 200) {
        Alert({ message: `Agent ${agent.isActive ? 'deactivated' : 'activated'} successfully`, variant: 'success' });
        onSuccess();
        onClose();
      }
    } catch (error) {
      Alert({ message: 'Failed to update agent status', variant: 'error' });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size='lg'
      hideCancelButton
      hideSuccessButton
    >
      <div className='p-4 md:p-6'>
        <div className='flex items-start justify-between mb-2'>
          <div className='space-y-1'>
            <h2 className='text-3xl font-extrabold text-gray-900 dark:text-white font-heading tracking-tight'>
              {isView ? 'Agent Details' : 'Edit Agent Details'}
            </h2>
            <p className='text-base text-gray-400 font-medium'>
              {isView ? 'View information about this travel agent.' : 'Update the agent\'s profile and company information.'}
            </p>
          </div>
          <span className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-wider ${agent.isActive ? 'bg-green-100 text-green-600 dark:bg-green-900/40' : 'bg-red-100 text-red-600 dark:bg-red-900/40'}`}>
            {agent.isActive ? 'ACTIVE' : 'INACTIVE'}
          </span>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, isValid, handleSubmit: formikSubmit }) => (
            <Form className='mt-10 space-y-8'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6'>
                <InputField
                  label='Username'
                  name='username'
                  placeholder='Enter username'
                  readOnly={isView}
                  customClass='!bg-gray-50 dark:!bg-[#232d3d] !text-gray-900 dark:!text-white !rounded-2xl !border-none !h-14 !px-6'
                  labelClass='!text-gray-500 dark:!text-gray-400 !font-bold !text-xs !mb-2'
                />
                <InputField
                  label='Email Address'
                  name='email'
                  type='email'
                  placeholder='Enter email'
                  readOnly={isView}
                  customClass='!bg-gray-50 dark:!bg-[#232d3d] !text-gray-900 dark:!text-white !rounded-2xl !border-none !h-14 !px-6'
                  labelClass='!text-gray-500 dark:!text-gray-400 !font-bold !text-xs !mb-2'
                />
                <InputField
                  label='Phone Number'
                  name='phoneNumber'
                  placeholder='Enter phone number'
                  readOnly={isView}
                  customClass='!bg-gray-50 dark:!bg-[#232d3d] !text-gray-900 dark:!text-white !rounded-2xl !border-none !h-14 !px-6'
                  labelClass='!text-gray-500 dark:!text-gray-400 !font-bold !text-xs !mb-2'
                />
                <InputField
                  label='Country'
                  name='country'
                  placeholder='Enter country'
                  readOnly={isView}
                  customClass='!bg-gray-50 dark:!bg-[#232d3d] !text-gray-900 dark:!text-white !rounded-2xl !border-none !h-14 !px-6'
                  labelClass='!text-gray-500 dark:!text-gray-400 !font-bold !text-xs !mb-2'
                />
                <InputField
                  label='Company Name'
                  name='companyName'
                  placeholder='Enter company name'
                  readOnly={isView}
                  customClass='!bg-gray-50 dark:!bg-[#232d3d] !text-gray-900 dark:!text-white !rounded-2xl !border-none !h-14 !px-6'
                  labelClass='!text-gray-500 dark:!text-gray-400 !font-bold !text-xs !mb-2'
                />
                <InputField
                  label='Company Website'
                  name='companyWebsite'
                  placeholder='Enter website URL'
                  readOnly={isView}
                  customClass='!bg-gray-50 dark:!bg-[#232d3d] !text-gray-900 dark:!text-white !rounded-2xl !border-none !h-14 !px-6'
                  labelClass='!text-gray-500 dark:!text-gray-400 !font-bold !text-xs !mb-2'
                />
              </div>

              <div className='pt-8 flex items-center justify-between gap-4 border-t border-gray-100 dark:border-gray-800 mt-10'>
                <div className='flex items-center gap-2 font-body'>
                  <span className='text-sm font-medium text-gray-500'>Status:</span>
                  <button
                    type='button'
                    onClick={handleToggleStatus}
                    disabled={isUpdatingStatus}
                    className={`text-sm font-bold transition-all hover:opacity-80 active:scale-95 ${agent.isActive ? 'text-red-500' : 'text-green-500'}`}
                  >
                    {(() => {
                      if (isUpdatingStatus) return 'Updating...';
                      return agent.isActive ? 'Deactivate Agent' : 'Activate Agent';
                    })()}
                  </button>
                </div>
                <div className='flex items-center gap-6'>
                  {!isView ? (
                    <>
                      <button
                        type='button'
                        onClick={onClose}
                        className='text-sm font-bold text-gray-500 hover:text-gray-900 transition-all font-body'
                      >
                        Cancel
                      </button>
                      <button
                        type='submit'
                        disabled={isSubmitting || !isValid}
                        onClick={() => formikSubmit()}
                        className='px-10 py-3 bg-[#10b981] text-white rounded-[2rem] text-sm font-extrabold shadow-lg shadow-primary/30 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:scale-100 transition-all font-heading'
                      >
                        {isSubmitting ? 'Saving...' : 'Update Agent'}
                      </button>
                    </>
                  ) : (
                    <button
                      type='button'
                      onClick={onClose}
                      className='px-10 py-3 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-[2rem] text-sm font-extrabold hover:bg-gray-200 dark:hover:bg-gray-700 transition-all font-heading'
                    >
                      Close
                    </button>
                  )}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
};

export default AgentEditModal;

